import React, { useState } from 'react';
import Svg from 'components/utils/Svg/Svg';
import { imageLinks } from 'assets/images';

import { useVoiceRecorder } from 'hooks/useVoiceRecorder';
import { useAudioVisualizer } from 'hooks/useAudioVisualizer';
import VoiceRecordingInterface from './VoiceRecordingInterface/VoiceRecordingInterface';
import "./ChatWidgetInput.scss";


interface ChatWidgetInputProps {
    onSendMessage: (message: string) => void;
    onSendVoiceMessage: (message: {
        content: Blob;
        duration: number;
    }) => void;
    onTypingActivity?: () => void;
    onVoiceRecordingStart?: () => void;
    onVoiceRecordingCancel?: () => void;
    disabled?: boolean;
}

const ChatWidgetInput: React.FC<ChatWidgetInputProps> = ({
    onSendMessage,
    onSendVoiceMessage,
    onTypingActivity,
    onVoiceRecordingStart,
    onVoiceRecordingCancel,
    disabled = false
}) => {

    const [textMessage, setTextMessage] = useState<string>('');
    const [showVoiceInterface, setVoiceInterfaceVisibility ] = useState<boolean>(false);

    const lineHeight = 14;
    const initialHeight = 32;
    const maxHeight = lineHeight * 4;

    // Voice recording logic
    const handleVoiceRecordingComplete = async (audioBlob: Blob, duration: number) => {
        console.log(`[Audio] Recording completed - Duration: ${duration}s, Size: ${audioBlob.size} bytes`);
    };

    const [voiceState, voiceActions] = useVoiceRecorder({
        onRecordingComplete: (audioBlob: Blob, duration: number) => { void handleVoiceRecordingComplete(audioBlob, duration); },
        onRecordingStart: () => {
            console.log('[Audio] Recording started - Capturing audio input...');
            onVoiceRecordingStart?.();
        },
        onRecordingStop: () => setVoiceInterfaceVisibility(false),
        onRecordingCancel: () => {
            onVoiceRecordingCancel?.();
            setVoiceInterfaceVisibility(false);
        },
        onRecordingPause: () => console.log('[Audio] Recording paused - Temporarily halted'),
        onRecordingResume: () => console.log('[Audio] Recording resumed - Continuing capture')
    });

    const { bars } = useAudioVisualizer(voiceState.isRecording && !voiceState.isPaused, voiceState.audioLevel);

    const sendVoiceMessage = (message: {
        content: Blob;
        duration: number;
    }) => {
        console.log(`[Audio] Sending voice message - Duration: ${message.duration}s, Size: ${message.content.size} bytes`);
        onSendVoiceMessage(message);
        console.log('[Audio] Voice message sent - Cleared audio state');
    }

    const calculateHeight = () => {
        const lines = Math.ceil(textMessage.length / 50);
        const height = Math.max(initialHeight, Math.min(lines * lineHeight, maxHeight));
        return height;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTextMessage(event.target.value);
        onTypingActivity?.();
    };

    const handleSendMessage = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
      
        if (textMessage.trim()) {
            onSendMessage(textMessage.trim());
            setTextMessage('');
        }
    };

    // Update handleSendAudio to get the audio blob directly from voiceState
    const handleSendAudio = async (e?: React.MouseEvent) => {
        e?.preventDefault();
        try {
            const { audioBlob, duration } = await voiceActions.getAudioData();
            console.log("audioBlob X duration", { audioBlob, duration });

            sendVoiceMessage({
                content: audioBlob,
                duration
            });
        } catch (error) {
            console.error('[Audio] Error getting audio data:', error);
        }
    };

    const showSendButton = textMessage.trim() && !showVoiceInterface;
    const showMicButton = !textMessage.trim() && !showVoiceInterface;


    return (
        <div className='chat__widget--input'>

            <form onSubmit={handleSendMessage} className="input__btn__group">
                {showVoiceInterface ? (
                    <VoiceRecordingInterface
                        onSubmit={handleSendAudio}
                        voiceState={voiceState}
                        voiceActions={voiceActions}
                        bars={bars}
                    />
                ) : (
                    <input
                        id="message"
                        type="text"
                        className="form-control"
                        placeholder='Write your message...'
                        value={textMessage}
                        onChange={handleInputChange}
                        style={{
                            height: `${calculateHeight()}px`,
                            overflow: 'hidden',
                        }}
                        disabled={disabled}
                    />)}

                {showSendButton && (
                    <button
                        type="submit"
                        className="send-button"
                        aria-label="Send message"
                        onClick={(e) => {
                            // Explicit onClick handler for React Native webview compatibility
                            e.preventDefault();
                            if (textMessage.trim()) {
                                onSendMessage(textMessage.trim());
                                setTextMessage('');
                            }
                        }}
                    >
                        <Svg src={imageLinks.icon.sendIcon} />
                    </button>
                )}

                {showMicButton && (
                    <button
                        type="button"
                        className="mic-button"
                        onClick={() => {
                            onVoiceRecordingStart?.();
                            setVoiceInterfaceVisibility(true);
                        }}
                        aria-label="Record voice message"
                    >
                        <Svg src={imageLinks.icon.micIcon} />
                    </button>
                )}
            </form>
        </div>
    );
};

export default ChatWidgetInput;
